import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router";
import SecretKeyDecryption from "./secretKeyDecryption";
import { passwordEncrypt } from "./passwordEncryption"; 
import { FaRegHospital } from "react-icons/fa";
import "./hm-theme.css";

function LabProfile({ selectedOrgcode }) {
  const { orgcode: paramOrgCode } = useParams();
  const orgcode = selectedOrgcode || paramOrgCode;
  const { fetchSecretKey } = SecretKeyDecryption();

  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const token = JSON.parse(localStorage.getItem("data"))?.token;

  const [orgProfile, setOrgProfile] = useState({
    username: "",
    labname: "",
    labcode: "",
    contact: "",
    description: "",
    pan: "",
    gstin: "",
    tan: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    pincode: "",
    state: "",
    createddate: "",
    latitude: "",
    longitude: "",
    regulated: false,
    labProfileImage: {
      image: null,
      imageMeta: "",
    }
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!orgcode) return;
      try {
        setLoading(true);
        const response = await axios.get(`/api/labdetails/getDetailsWithProfileImageByLabcode/labcode=${orgcode}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrgProfile(response.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        handleError(error);
      }
    };

    fetchProfile();
  }, [orgcode]);

  function getBase64FromFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = function () {
        const [metadata, content] = reader.result.split(",");
        resolve({ metadata, content });
      };
      reader.onerror = function (error) {
        reject(error);
      };
    });
  }

  const handleImageChange = async (event) => {
    if (event.target.files && event.target.files[0]) {
      const convertedImage = await getBase64FromFile(event.target.files[0]);
      setOrgProfile((prevState) => ({
        ...prevState,
        labProfileImage: {
          imageMeta: convertedImage.metadata,
          image: convertedImage.content,
        },
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      await axios.post(`/api/labdetails/update`, orgProfile, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Lab Profile Updated Successfully");
      setLoading(false);
    } catch (error) {
      setLoading(false);
      handleError(error);
    }
  };

  const navigate = useNavigate();
  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data && error.response.data.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error("An error occurred.");
    }
  }

  const handleChange = (event, field) => {
    let actualValue = event.target.value;
    setOrgProfile({ ...orgProfile, [field]: actualValue });
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const decryptedSecretKey = await fetchSecretKey();
      if (!decryptedSecretKey) {
        setLoading(false);
        return;
      }

      const encryptedPassword = await passwordEncrypt(password, decryptedSecretKey);
      const body = {
        id: orgProfile.userid,
        password: encryptedPassword,
      };

      await axios.post(`/api/accounts/update-password`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Password Updated Successfully");
      setPassword("");
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <div className="loading"></div>}

      <div className="hm-card mb-4">
        <div className="hm-card-header">
          <h3 className="hm-card-title">
            <i className="material-icons">biotech</i>
            Lab Profile
          </h3>
        </div>

        <div className="hm-card-body p-4">
          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              {/* Left Column: Profile Picture */}
              <div className="col-md-3 text-center">
                <div className="d-flex flex-column align-items-center">
                  {orgProfile.labProfileImage?.image ? (
                    <img
                      className="img-fluid rounded-circle border shadow-sm mb-3"
                      src={`${orgProfile.labProfileImage?.imageMeta},${orgProfile.labProfileImage?.image}`}
                      alt="Lab Logo"
                      style={{ width: "140px", height: "140px", objectFit: "cover" }}
                    />
                  ) : (
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center bg-light border text-primary shadow-sm mb-3"
                      style={{ width: "140px", height: "140px", fontSize: "50px" }}
                    >
                      <FaRegHospital />
                    </div>
                  )}

                  <label className="btn btn-outline-primary btn-sm rounded-pill px-3 cursor-pointer">
                    <i className="material-icons align-middle me-1" style={{ fontSize: "16px" }}>photo_camera</i>
                    Upload Logo
                    <input
                      type="file"
                      className="d-none"
                      onChange={handleImageChange}
                      accept="image/*"
                    />
                  </label>
                </div>
              </div>

              {/* Right Column: Form Inputs */}
              <div className="col-md-9">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="labname" className="form-label small fw-semibold">
                      Lab Name <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      id="labname"
                      type="text"
                      value={orgProfile.labname}
                      onChange={(e) => handleChange(e, "labname")}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="labcode" className="form-label small fw-semibold">
                      Lab Code <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      id="labcode"
                      type="text"
                      value={orgProfile.labcode}
                      onChange={(e) => handleChange(e, "labcode")}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="username" className="form-label small fw-semibold">
                      Email ID <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control bg-light"
                      id="username"
                      type="email"
                      value={orgProfile.username}
                      readOnly
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="contact" className="form-label small fw-semibold">
                      Contact Number <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      id="contact"
                      type="text"
                      value={orgProfile.contact}
                      onChange={(e) => handleChange(e, "contact")}
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="gstin" className="form-label small fw-semibold">GSTIN</label>
                    <input
                      className="form-control"
                      id="gstin"
                      type="text"
                      value={orgProfile.gstin}
                      onChange={(e) => handleChange(e, "gstin")}
                    />
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="pan" className="form-label small fw-semibold">PAN</label>
                    <input
                      className="form-control"
                      id="pan"
                      type="text"
                      value={orgProfile.pan}
                      onChange={(e) => handleChange(e, "pan")}
                    />
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="tan" className="form-label small fw-semibold">TAN</label>
                    <input
                      className="form-control"
                      id="tan"
                      type="text"
                      value={orgProfile.tan}
                      onChange={(e) => handleChange(e, "tan")}
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="addressLine1" className="form-label small fw-semibold">
                      Address Line 1 <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      id="addressLine1"
                      type="text"
                      value={orgProfile.addressLine1}
                      onChange={(e) => handleChange(e, "addressLine1")}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="addressLine2" className="form-label small fw-semibold">Address Line 2</label>
                    <input
                      className="form-control"
                      id="addressLine2"
                      type="text"
                      value={orgProfile.addressLine2}
                      onChange={(e) => handleChange(e, "addressLine2")}
                    />
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="city" className="form-label small fw-semibold">
                      City <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      id="city"
                      type="text"
                      value={orgProfile.city}
                      onChange={(e) => handleChange(e, "city")}
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="pincode" className="form-label small fw-semibold">
                      Pincode <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      id="pincode"
                      type="text"
                      value={orgProfile.pincode}
                      onChange={(e) => handleChange(e, "pincode")}
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="state" className="form-label small fw-semibold">
                      State <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      id="state"
                      type="text"
                      value={orgProfile.state}
                      onChange={(e) => handleChange(e, "state")}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="latitude" className="form-label small fw-semibold">Latitude</label>
                    <input
                      className="form-control"
                      id="latitude"
                      type="text"
                      placeholder="e.g. 12.9716"
                      value={orgProfile.latitude}
                      onChange={(e) => handleChange(e, "latitude")}
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="longitude" className="form-label small fw-semibold">Longitude</label>
                    <input
                      className="form-control"
                      id="longitude"
                      type="text"
                      placeholder="e.g. 77.5946"
                      value={orgProfile.longitude}
                      onChange={(e) => handleChange(e, "longitude")}
                    />
                  </div>

                  <div className="col-md-12 pt-2">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="regulatedToggle"
                        checked={orgProfile.regulated}
                        onChange={(e) => handleChange({ target: { value: e.target.checked } }, "regulated")}
                        style={{ cursor: "pointer" }}
                      />
                      <label className="form-check-label fw-semibold ms-2" htmlFor="regulatedToggle" style={{ cursor: "pointer" }}>
                        {orgProfile.regulated ? "Regulated Lab" : "Unregulated Lab"}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <button type="submit" className="btn-hm-primary" disabled={loading}>
                    <i className="material-icons" style={{ fontSize: "1rem" }}>save</i>
                    Save Profile Changes
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Password Card */}
      <div className="hm-card">
        <div className="hm-card-header">
          <h4 className="hm-card-title">
            <i className="material-icons">lock</i>
            Update Lab Password
          </h4>
        </div>
        <div className="hm-card-body p-4">
          <form onSubmit={handlePasswordSubmit} className="row g-3 align-items-end">
            <div className="col-md-6 col-lg-5">
              <label htmlFor="password" className="form-label small fw-semibold">New Password</label>
              <input
                className="form-control"
                id="password"
                type="password"
                placeholder="Enter new password..."
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="col-md-4">
              <button type="submit" className="btn-hm-primary" disabled={loading || !password}>
                <i className="material-icons" style={{ fontSize: "1rem" }}>key</i>
                Update Password
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LabProfile;